




























































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import _ from "@/boot/lodash";

// Enums/Data
import { TrialEndActionTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data";

// Types
import {
  IProduct,
  IProductTrialForm,
  ITrialMigrationRule
} from "@/models/products";
import { IBillingCycle } from "@/models/constants";

@Component({
  components: {
    "product-list-item-switcher": () =>
      import(
        "@/components/app/admin/catalogue/products/listing/productListItemSwitcher.vue"
      )
  }
})
export default class TrialForm extends Vue {
  // Props

  @Prop({ required: true })
  public value!: IProductTrialForm;

  @Prop({ required: true })
  public formData!: IProductTrialForm;

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ required: true })
  public billingCycles!: IBillingCycle[];

  @Prop({ default: null })
  public trialNewProduct!: IProduct;

  // Data

  public trialForm = _.cloneDeep(this.initiateForm());
  public endActionOptions = [
    {
      leftLabel: this.$t("_.continue"),
      desc: this.$t("_sentence.continue_using_paid_version_desc"),
      value: TrialEndActionTypes.CONTINUE
    },
    {
      leftLabel: this.$t("_.migrate"),
      desc: this.$t("_sentence.migrate_to_a_new_product_desc"),
      value: TrialEndActionTypes.MIGRATE
    },
    {
      leftLabel: this.$t("_.cancel"),
      desc: this.$t("_sentence.cancel_product_trial_desc"),
      value: TrialEndActionTypes.CANCEL
    }
  ];
  public selectedProduct = {} as IProduct;

  // Life-cycle methods

  public created() {
    this.initiateSelectedProduct();
    this.$emit("initiateForm");
  }

  public beforeDestroy() {
    this.$emit("initiateForm");
  }

  // Methods

  public initiateForm() {
    return {
      trial_force: this.formData.trial_force || false,
      trial_supported: this.formData.trial_supported || false,
      trial_duration: this.formData.trial_duration || 7,
      trial_end_action: this.formData.trial_end_action || 0,
      trial_pre_expire_notification:
        this.formData.trial_pre_expire_notification || 1,
      ...((this.formData.trial_end_action === TrialEndActionTypes.MIGRATE
        ? {
            trial_migration_rule: this.formData.trial_migration_rule || {}
          }
        : {}) as ITrialMigrationRule)
    };
  }

  public selectProduct() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openSelectProductsModal`,
      {
        parent: this,
        props: {
          isMultiselect: false,
          manualFilters: {
            "filter[id|neq]": this.productId,
            "filter[billing_cycle_months|neq]": 0,
            "filter[prices.billing_cycle_months|gt]": 0
          }
        },
        events: {
          success: selectedProducts => {
            this.selectedProduct = selectedProducts[0];

            const prices = _.sortBy(
              _.uniqBy(
                _.filter(
                  _.get(this.selectedProduct, "prices", []),
                  price => price.billing_cycle_months > 0
                ),
                "billing_cycle_months"
              ),
              "billing_cycle_months"
            );

            this.$set(this.trialForm, "trial_migration_rule", {
              new_product_id: _.get(this.selectedProduct, "id"),
              new_product_billing_cycle_months: _.get(
                prices,
                "[0].billing_cycle_months",
                1
              )
            });
          }
        }
      }
    );
  }

  public unselectProduct() {
    this.selectedProduct = {} as IProduct;
    this.$set(this.trialForm, "trial_migration_rule", {});
  }

  public initiateSelectedProduct() {
    if (
      !_.isNull(this.trialNewProduct) &&
      this.trialForm.trial_end_action === TrialEndActionTypes.MIGRATE
    ) {
      this.$set(this, "selectedProduct", this.trialNewProduct);
    }
  }

  public revertForm() {
    this.initiateSelectedProduct();
    this.$set(this, "trialForm", _.cloneDeep(this.initiateForm()));
    this.$emit("initiateForm");
  }

  // Computed

  public get showSelectedProduct() {
    return this.trialForm.trial_end_action === TrialEndActionTypes.MIGRATE;
  }

  public get showAddProductButton() {
    return _.isEmpty(this.selectedProduct);
  }

  public get hasProvisioning() {
    return this.$store.getters["org/hasProvisioningEnabled"];
  }

  // DO NOT REMOVE! - used by parent form component
  public get initialForm() {
    const initialForm = this.initiateForm();
    if (!initialForm.trial_supported) return { trial_supported: false };
    return initialForm;
  }

  public get selectedProductTerms() {
    return _.map(
      _.orderBy(
        _.uniqBy(
          _.filter(
            this.selectedProduct.prices || [],
            price => price.billing_cycle_months > 0
          ),
          "billing_cycle_months"
        ),
        ["billing_cycle_months"]
      ),
      term => {
        const billingCycle = _.find(this.billingCycles, [
          "months",
          term.billing_cycle_months
        ]) as IBillingCycle;

        return {
          value: billingCycle.months || term.billing_cycle_months,
          label:
            billingCycle.name ||
            this.$t("_.every_months", {
              months: term.billing_cycle_months
            }).toString()
        };
      }
    );
  }

  // Watchers

  @Watch("trialForm", { deep: true, immediate: true })
  public onTrialFormChange(newVal) {
    if (!newVal.trial_supported)
      return this.$emit("input", { trial_supported: false });

    if (
      this.trialForm.trial_end_action !== TrialEndActionTypes.MIGRATE ||
      _.isEmpty(_.get(this.trialForm, "trial_migration_rule", {}))
    ) {
      return this.$emit("input", _.omit(newVal, ["trial_migration_rule"]));
    }

    return this.$emit("input", newVal);
  }
}
